import React from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting'
import {
  XAxisColumnOption,
  DateGroupingOption,
  YAxisColumnOption,
  YAxisAggregationOption,
  YAxisLabelOption,
  YAxisGroupByOption,
  ShowTotalsOption
} from './chart-options'
import { drillDownPlotOptions } from './drilldown'

HighchartsExporting(Highcharts)
HighchartsOfflineExporting(Highcharts)

function LineChart({
  series,
  title = '',
  subtitle = '',
  categories,
  tooltipFormatter,
  yAxisTitle = '',
  hasGrouping = false,
  showTotals = false,
  colors = null,
  chartRef = null,
  onPointClick = null
}) {
  // Handle both single series (array of numbers) and multiple series (array of series objects)
  let chartSeries = hasGrouping ? series : [{
    name: 'Data',
    data: series.map((value) => typeof value === 'number' ? { y: value } : value),
    showInLegend: false
  }]

  // Backend prepends a "Total" series for grouped data; hide it unless showTotals is on
  if (hasGrouping && !showTotals && chartSeries.length > 0 && chartSeries[0]?.name === 'Total') {
    chartSeries = chartSeries.slice(1)
  }
  const options = {
    ...(colors && { colors }), // Apply brand colors if provided
    chart: {
      type: 'line',
      zoomType: 'x'
    },
    title: {
      text: title
    },
    subtitle: {
      text: subtitle
    },
    xAxis: {
      type: 'category',
      categories: categories
    },
    yAxis: {
      title: {
        text: yAxisTitle || 'Count (n)'
      },
      allowDecimals: false
    },
    tooltip: {
      shared: true,
      formatter: tooltipFormatter
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: true,
      filename: title || 'chart',
      fallbackToExportServer: false,
      libURL: '', // Prevent loading any external libraries
      buttons: {
        contextButton: {
          enabled: false
        }
      }
    },
    series: chartSeries,
    plotOptions: {
      ...drillDownPlotOptions(onPointClick),
      column: {
        dataLabels: {
          enabled: true,
          format: '{y}'
        }
      },
      line: {
        marker: {
          enabled: hasGrouping ? true : false
        }
      }
    },
    legend: {
      enabled: hasGrouping
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600
          }
        }
      ]
    }
  }

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} updateArgs={[true, true, false]} />
}

LineChart.propTypes = {
  series: PropTypes.array.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  yAxisTitle: PropTypes.string,
  hasGrouping: PropTypes.bool,
  showTotals: PropTypes.bool,
  colors: PropTypes.arrayOf(PropTypes.string),
  chartRef: PropTypes.object,
  onPointClick: PropTypes.func
}

// Chart configuration for line charts
export const lineChartConfig = {
  requiredFields: ['title', 'xAxisColumn', 'yAxisColumn'],

  validate: (formData) => {
    const errors = {}
    if (!formData.xAxisColumn) {
      errors.xAxisColumn = 'X-Axis column is required for line charts'
    }
    if (!formData.yAxisColumn) {
      errors.yAxisColumn = 'Y-Axis column is required for line charts'
    }
    return errors
  },

  isQueryEnabled: (formData, hasChartId) => {
    return !!(hasChartId && formData.xAxisColumn && formData.yAxisColumn)
  },

  buildChartParams: (formData) => ({
    group_by_column_id: formData.groupBy,
    x_axis_column_id: formData.xAxisColumn,
    x_axis_date_grouping: formData.xAxisDateGrouping,
    y_axis_column_id: formData.yAxisColumn,
    y_axis_aggregation: formData.yAxisAggregation,
    y_axis_label: formData.yAxisLabel,
    y_axis_group_by_column_id: formData.yAxisGroupBy,
    show_totals: formData.showTotals
  }),

  // Build query params for API request
  buildQueryParams: (baseParams, formData, columns) => {
    const xAxisCol = columns?.find((c) => c.id === formData.xAxisColumn)
    const params = { ...baseParams }
    params.group_by = xAxisCol?.database_column_name
    if (formData.xAxisDateGrouping && xAxisCol?.display_as === 'date') {
      params.date_grouping = formData.xAxisDateGrouping
    }
    if (formData.yAxisColumn) {
      const yAxisCol = columns?.find((c) => c.id === formData.yAxisColumn)
      params.y_axis_column = yAxisCol?.database_column_name
      params.y_axis_aggregation = formData.yAxisAggregation || 'count'
    }
    const yAxisGroupByCol = columns?.find((c) => c.id === formData.yAxisGroupBy)
    params.y_axis_group_by = yAxisGroupByCol?.database_column_name
    return params
  },

  // Supports date formatting
  supportsDateFormatting: true,

  // Render chart-specific options in the modal form
  renderOptions: ({
    Form,
    formState,
    setters,
    validationErrors,
    hasSubmitted,
    columns,
    getGroupingColumns,
    getValueColumns,
    getAggregationOptions,
    getColumnDisplayName,
    getAggregationLabel,
    municipality
  }) => {
    const {
      xAxisColumn,
      xAxisDateGrouping,
      yAxisColumn,
      yAxisAggregation,
      yAxisLabel,
      yAxisGroupBy,
      showTotals
    } = formState

    const {
      setXAxisColumn,
      setXAxisDateGrouping,
      setYAxisColumn,
      setYAxisAggregation,
      setYAxisLabel,
      setYAxisGroupBy,
      setShowTotals,
      setValidationErrors
    } = setters

    return (
      <div className="mb-3">
        <XAxisColumnOption
          Form={Form}
          xAxisColumn={xAxisColumn}
          setXAxisColumn={setXAxisColumn}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
          filterByDate={true}
        />

        <DateGroupingOption
          Form={Form}
          xAxisColumn={xAxisColumn}
          xAxisDateGrouping={xAxisDateGrouping}
          setXAxisDateGrouping={setXAxisDateGrouping}
          columns={columns}
        />

        <YAxisColumnOption
          Form={Form}
          yAxisColumn={yAxisColumn}
          setYAxisColumn={setYAxisColumn}
          yAxisAggregation={yAxisAggregation}
          setYAxisAggregation={setYAxisAggregation}
          validationErrors={validationErrors}
          setValidationErrors={setValidationErrors}
          hasSubmitted={hasSubmitted}
          getValueColumns={getValueColumns}
          getAggregationOptions={getAggregationOptions}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        <YAxisAggregationOption
          Form={Form}
          yAxisAggregation={yAxisAggregation}
          setYAxisAggregation={setYAxisAggregation}
          yAxisColumn={yAxisColumn}
          getAggregationOptions={getAggregationOptions}
          getAggregationLabel={getAggregationLabel}
        />

        <YAxisLabelOption
          Form={Form}
          yAxisLabel={yAxisLabel}
          setYAxisLabel={setYAxisLabel}
        />

        <YAxisGroupByOption
          Form={Form}
          yAxisGroupBy={yAxisGroupBy}
          setYAxisGroupBy={setYAxisGroupBy}
          getGroupingColumns={getGroupingColumns}
          getColumnDisplayName={getColumnDisplayName}
          municipality={municipality}
        />

        {yAxisGroupBy && (
          <ShowTotalsOption
            Form={Form}
            showTotals={showTotals}
            setShowTotals={setShowTotals}
          />
        )}
      </div>
    )
  }
}

// React.memo so we don't re-render on parent updates when props haven't
// changed — prevents HighchartsReact from calling chart.update() mid-animation
// after a tab toggle / background refetch.
export default React.memo(LineChart)
